/* eslint-disable react/jsx-props-no-spreading */
import React, { useEffect } from 'react';
import * as Sentry from '@sentry/react';
import { NextPagesNavigator } from '@meitu/catalyze-next';
import Head from 'next/head';
import Script from 'next/script';
import { extractUTMParams } from '@meitu/tracer-utm-params';
import { createCommonParamsMiddleware } from '$/api/middlewares/createCommonParamsMiddleware';
import { getIsOverseaByCountryCode } from '$/common/utils/countryCodeUtil';
import localStorageUtil from '$/common/utils/localStorageUtil';
import { useLanguageStore, LanguageCodeType } from '$/store/languageStore';
import { InitComponent, InitNavigateMap } from '$/store/useOfficialStore';
import {
  getChannel, getMatrixChannel, initChannel, getFirstURL,
} from '$/utils/channelUtil';

import {
  getEnv, navigate, pageAgent, statAgent,
} from '@/common/agents';
import { defineNextSubApp, renderNextPage } from '@/common/next';
import { buildTikTokContext, collectTikTokContext, getGid } from '@/utils';
import { createErrorMiddleware } from '@/apiSdk/errorMiddleware';
import { createCanvasErrorMiddleware } from '@/apiSdk/errorCanvasMiddleware';
import { initMTLogin, getUID, getAccessToken } from '@/common/utils/accountUtil';
import { loadMtstatSdkScript } from '@/common/loadMtstatSdkScript';
import { setCurrentPathname } from '@/store/useCommonStore';
import { useRouter } from 'next/router';
import { Config } from '@/common/config';
import { LanguageCode } from '$/store/languageStore/types';
import { isInLangList, mapLanguageCode } from '$/store/languageStore/utils';
import useTeamStore, { getTeamTrackCommonParams, subscribeTeamStoreFromStorage } from '@/store/useTeamStore';
import MyIpReturnStateHost from '@/components/MyIpReturnStateHost';
import favicon from './favicon.ico';
import { navigateWithTeamStudioCover } from './team_studio/utils/teamStudioRouteCover';

/** App 内埋点用 gid 的存储 key，beforePageView 读取；MTJs 就绪后取到 gid 会写入并调用 mtstat.setGid */
const APP_GID_FOR_STAT_KEY = '__ROBONEO_APP_GID__';

/** TikTok Pixel ID，填入真实 Pixel Code 后会加载 TikTok Pixel */
const TIKTOK_PIXEL_CODE = 'D880LC3C77U8EJN007E0';

/** Google Analytics ID，国内场景不传入也不加载脚本 */
const GOOGLE_TARGET_ID = 'G-GJ1BR2FCKG';

/** 等待端上注入 MTJs（与 hot_feed 一致：WebviewJsBridgeReady 或已有 window.MTJs），带超时避免非 App 环境一直等 */
function waitForMTJsSdkReady(timeoutMs = 5000): Promise<void> {
  return new Promise((resolve) => {
    if (typeof window === 'undefined') {
      resolve();
      return;
    }
    if ((window as any).MTJs) {
      resolve();
      return;
    }
    const done = () => {
      clearTimeout(timer);
      window.removeEventListener('WebviewJsBridgeReady', onReady);
      resolve();
    };
    const onReady = () => done();
    window.addEventListener('WebviewJsBridgeReady', onReady);
    const timer = setTimeout(done, timeoutMs);
  });
}

/** 渲染站点外层，并按地区控制海外三方分析脚本加载 */
function Content({ appProps }: any) {
  const languageStore = useLanguageStore((state) => state.languageStore);
  const { userAgent, theme, countryCode } = appProps.pageProps;
  // 嵌入到端上的hot_feed webview初始化不展示 title
  const isMeituAPP = /com.meitu|com.starii.xdesign/i.test(userAgent); // 检测是否来自美图APP
  const shouldLoadOverseaAnalytics = getIsOverseaByCountryCode(countryCode);
  // eslint-disable-next-line ban/ban
  const router = useRouter();
  const handleRouteChange = (pageName: string) => {
    window.mtstat?.pageview({
      title: document.title,
      url: window.location.href,
      station: 0,
    });
    const currentPathname = pageName.split('?')[0];
    setCurrentPathname(`${/--roboneo/.test(currentPathname) ? '' : '/--roboneo'}${currentPathname}` as Config['Pathname']);
  };

  useEffect(() => {
    setCurrentPathname(appProps.router.pathname);
    router.events.on('routeChangeComplete', handleRouteChange);
    return () => {
      router.events.off('routeChangeComplete', handleRouteChange);
    };
  }, [router.events]);

  // 跨标签页同步 useTeamStore（其他标签页切换团队时，当前页通过 storage 事件重新水合）
  useEffect(() => subscribeTeamStoreFromStorage(), []);

  // App 内：等 MTJs 注入后再取 getAppInfo 的 gid，并主动调用一次 mtstat.setGid，后续埋点即用 App gid
  useEffect(() => {
    const inApp = typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('in_app') === 'true';
    if (!inApp) return;
    waitForMTJsSdkReady()
      .then(async () => {
        try {
          // @ts-expect-error 模块由 roboneo-web 间接依赖，类型在应用层可能未解析
          const { getAppInfo } = await import('@meitu/hogger');
          const res = await getAppInfo();
          const first = Array.isArray(res) ? res[0] : res;
          const gid = (first && typeof first === 'object' && (first as any).gid) ? (first as any).gid : null;
          if (gid && typeof window !== 'undefined') {
            (window as any)[APP_GID_FOR_STAT_KEY] = gid;
            window.mtstat?.setGid(gid, 'app');
          }
        } catch {
          // 非 App 或 hogger 异常，beforePageView 继续用 getGid() 兜底
          console.log('hogger 不可用');
        }
      });
  }, []);
  return (
    <>
      <MyIpReturnStateHost />
      <Head>

        {/*  Google tag (gtag.js) */}
        {/* <Script
          id="google-analytics-script"
          src="https://www.googletagmanager.com/gtag/js?id=G-GJ1BR2FCKG"
          strategy="afterInteractive"
        />
        <script>
          {`
            window.dataLayer = window.dataLayer || [];
            function gtag(){window.dataLayer.push(arguments);}
            gtag('js', new Date());
            gtag('config', 'G-GJ1BR2FCKG');
          `}
        </script> */}
        <link rel="icon" href={favicon.src} sizes="any" />
        <title>
          {/* RoboNeo - 专注影像与设计的AI助手 */}
          {isMeituAPP ? '' : languageStore.smartcatTranslationText((v) => v.RoboNeo_Web_share_card_title)}
        </title>
        <meta
          name="description"
          content={languageStore.smartcatTranslationText((v) => v.RoboNeo_Web_share_card_content)}
        />
        <meta property="og:image" content={favicon.src} />
        <style>
          {`
        html, body {
          background: ${theme === 'light' ? '#f0f0f0ff' : '#0a0a0aff'} !important;
        }
     `}
        </style>

      </Head>
      {shouldLoadOverseaAnalytics ? (
        <>
          <Script
            id="facebook-pixel"
            strategy="afterInteractive"
            dangerouslySetInnerHTML={{
              __html: `
                  !function(f,b,e,v,n,t,s)
                  {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
                  n.callMethod.apply(n,arguments):n.queue.push(arguments)};
                  if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
                  n.queue=[];t=b.createElement(e);t.async=!0;
                  t.src=v;s=b.getElementsByTagName(e)[0];
                  s.parentNode.insertBefore(t,s)}(window, document,'script',
                  'https://connect.facebook.net/en_US/fbevents.js');
                  fbq('init', '874991374996562');
                  fbq('track', 'PageView');
                `,
            }}
          />
          <noscript>
            <img
              height="1"
              width="1"
              style={{ display: 'none' }}
              src="https://www.facebook.com/tr?id=874991374996562&ev=PageView&noscript=1"
              alt=""
            />
          </noscript>
        </>
      ) : null}

      {shouldLoadOverseaAnalytics && TIKTOK_PIXEL_CODE ? (
        <Script
          id="tiktok-pixel"
          strategy="afterInteractive"
          dangerouslySetInnerHTML={{
            __html: `
              !function (w, d, t) {
                w.TiktokAnalyticsObject = t;
                var ttq = w[t] = w[t] || [];
                ttq.methods = [
                  "page", "track", "identify", "instances", "debug",
                  "on", "off", "once", "ready", "alias", "group",
                  "enableCookie", "disableCookie", "holdConsent",
                  "revokeConsent", "grantConsent"
                ];
                ttq.setAndDefer = function (target, method) {
                  target[method] = function () {
                    target.push([method].concat(Array.prototype.slice.call(arguments, 0)));
                  };
                };
                for (var i = 0; i < ttq.methods.length; i++) {
                  ttq.setAndDefer(ttq, ttq.methods[i]);
                }
                ttq.instance = function (pixelCode) {
                  var instance = ttq._i[pixelCode] || [];
                  for (var i = 0; i < ttq.methods.length; i++) {
                    ttq.setAndDefer(instance, ttq.methods[i]);
                  }
                  return instance;
                };
                ttq.load = function (pixelCode, options) {
                  var scriptUrl = "https://analytics.tiktok.com/i18n/pixel/events.js";
                  ttq._i = ttq._i || {};
                  ttq._i[pixelCode] = [];
                  ttq._i[pixelCode]._u = scriptUrl;
                  ttq._t = ttq._t || {};
                  ttq._t[pixelCode] = +new Date();
                  ttq._o = ttq._o || {};
                  ttq._o[pixelCode] = options || {};
                  var script = d.createElement("script");
                  script.type = "text/javascript";
                  script.async = true;
                  script.src = scriptUrl + "?sdkid=" + pixelCode + "&lib=" + t;
                  var firstScript = d.getElementsByTagName("script")[0];
                  firstScript.parentNode.insertBefore(script, firstScript);
                };
              }(window, document, "ttq");

              ttq.load(${JSON.stringify(TIKTOK_PIXEL_CODE)});
              ttq.page();
            `,
          }}
        />
      ) : null}

      {shouldLoadOverseaAnalytics ? (
        <>
          {/* 将 Script 组件移到 Head 外面 */}
          <Script
            id="google-analytics-script"
            src={`https://www.googletagmanager.com/gtag/js?id=${GOOGLE_TARGET_ID}`}
            strategy="afterInteractive"
          />
          <Script
            id="google-analytics-init"
            strategy="afterInteractive"
            dangerouslySetInnerHTML={{
              __html: `
              window.dataLayer = window.dataLayer || [];
              function gtag(){window.dataLayer.push(arguments);}
              gtag('js', new Date());
              gtag('config', ${JSON.stringify(GOOGLE_TARGET_ID)});
            `,
            }}
          />
        </>
      ) : null}
      {/* 该组件是必需的，否则navigate()不会生效。 */}
      <NextPagesNavigator navigate={navigate} />
      {renderNextPage(appProps)}
    </>
  );
}
export default defineNextSubApp<{
  InitialData: {};
}>({
  component: ({ appProps, initialData }) => {
    // appProps.pageProps.areaCode是服务端渲染时的ip语言，systemLocale是水合前判断出的系统语言

    return (
      <Content appProps={appProps} initialData={initialData} />
    );
  },
  // 在浏览器内、水合开始之前、也就是用户可以与页面交互之前，完成埋点初始化等操作。
  // 这是必要的。
  async beforeHydration(data: any) {
    initChannel();
    const userLang = localStorageUtil.getItem('lang');
    const searchParams = new URLSearchParams(window.location.search);
    const roomId = searchParams.get('room_id') || '';
    const projectId = searchParams.get('project_id') || '';
    const shouldLoadOverseaAnalytics = getIsOverseaByCountryCode(data.props.pageProps.countryCode);
    let lang: LanguageCodeType = shouldLoadOverseaAnalytics ? LanguageCode.en : LanguageCode.zhHans;
    const urlLanguageCode = searchParams.get('language_code');
    const acceptLang = navigator.language.toLowerCase();
    const inApp = searchParams.get('in_app') === 'true';
    // 链接参数
    if (isInLangList(urlLanguageCode as LanguageCodeType)) {
      lang = mapLanguageCode(urlLanguageCode as LanguageCodeType);
    // local缓存参数
    } else if (isInLangList(userLang as LanguageCodeType)) {
      lang = mapLanguageCode(userLang as LanguageCodeType);
    // 浏览参数
    } else if (acceptLang) {
      console.log(acceptLang, 'acceptLang');
      lang = mapLanguageCode(acceptLang as LanguageCodeType);
    }
    const channelValue = getChannel();
    const utm = extractUTMParams();
    const tikTokContext = buildTikTokContext();
    // page_start 由 statAgent 内部 pageview 上报，初始化前先缓存 App gid 供 beforePageView 使用。
    if (typeof window !== 'undefined') {
      (window as any)[APP_GID_FOR_STAT_KEY] = null;
    }
    if (inApp && typeof window !== 'undefined') {
      await waitForMTJsSdkReady(2000);
      if ((window as any).MTJs) {
        try {
          // @ts-expect-error 模块由 roboneo-web 间接依赖，类型在应用层可能未解析
          const { getAppInfo } = await import('@meitu/hogger');
          const res = await getAppInfo({ timeout: 2000 });
          const first = Array.isArray(res) ? res[0] : res;
          const gid = (first && typeof first === 'object' && (first as any).gid) ? (first as any).gid : null;
          if (gid) {
            (window as any)[APP_GID_FOR_STAT_KEY] = gid;
          }
        } catch {
          console.log('hogger 不可用');
        }
      }
    }
    const teamTrackCommonParams = getTeamTrackCommonParams(useTeamStore.getState().teamInfo);
    await loadMtstatSdkScript();
    await statAgent.initialize({
      appKey: process.env.NEXT_PUBLIC_REBONEO_APP_KEY as string,
      uid: getUID(),
      env: getEnv().ENV === 'release' ? 'release' : 'test',
      channel: channelValue,
      moduleId: 'roboneo_web',
      commonParams: {
        utm_info: utm?.statsParams?.utm_info || '',
        room_id: roomId,
        project_id: projectId,
        ...teamTrackCommonParams,
        first_url: tikTokContext.first_url || getFirstURL(), // 渠道投放的落地页url
        tt_ttclid: tikTokContext.ttclid,
        tt_ttp: tikTokContext.ttp,
        page_url: tikTokContext.page_url,
        referrer: tikTokContext.referrer,
        pixel_ready: tikTokContext.pixel_ready ? '1' : '0',
        channel: channelValue,
        matrix_channel: getMatrixChannel(), // 矩阵跳转时的标识
        sys_language: lang,
      },
      beforeLogin() { return true; },
      beforePageView(mtstat) {
        const appGid = typeof window !== 'undefined' ? (window as any)[APP_GID_FOR_STAT_KEY] : null;
        mtstat.setGid(inApp ? (appGid ?? getGid()) : getGid(), 'app');
        return true;
      },
    });
    collectTikTokContext((context) => {
      window.mtstat?.setCommonParams({
        first_url: context.first_url || getFirstURL(),
        tt_ttclid: context.ttclid,
        tt_ttp: context.ttp,
        page_url: context.page_url,
        referrer: context.referrer,
        pixel_ready: context.pixel_ready ? '1' : '0',
      });
    });
    const shareCoverUrl = getEnv().ENV === 'development'
      ? `${window.location.origin}/--roboneo/share_cover`
      : `${window.location.origin}/share_cover`;

    const shareFlowUrl = getEnv().ENV === 'development'
      ? `${window.location.origin}/--roboneo/flow_share`
      : `${window.location.origin}/flow_share`;

    const shareTeamUrl = getEnv().ENV === 'development'
      ? `${window.location.origin}/--roboneo/team_share`
      : `${window.location.origin}/team_share`;

    const { theme } = data.props.pageProps;
    const TOKEN = {
      development: 'A2600861ECF8CBF270D24AE3AD37F4BA',
      dev: 'A2600861ECF8CBF270D24AE3AD37F4BA',
      pre: 'A2600861ECF8CBF270D24AE3AD37F4BA',
      beta: '315F4D4D9343434994287A78EAC04076',
      release: '45C30555F10E49629098A75F95828DA6',
    };

    const token = getAccessToken();
    const uid = getUID();
    const currentTeamInfo = useTeamStore.getState().teamInfo;
    InitComponent({
      clientOId: currentTeamInfo?.oid,
      clientOGroupId: currentTeamInfo?.client_ogroupid,
      clientOTeamName: currentTeamInfo?.name,
      clientOIsAdmin: currentTeamInfo ? Number(currentTeamInfo.role) === 1 : undefined,
      clientId: process.env.NEXT_PUBLIC_REBONEO_CLIENT_ID || '',
      env: getEnv().ENV,
      gid: getGid(),
      token: TOKEN[getEnv().ENV],
      firstUrl: getFirstURL(), // 渠道投放的落地页url
      channel: channelValue,
      shareCoverUrl,
      shareFlowUrl,
      shareTeamUrl,
      currentCountryCode: data.props.pageProps.countryCode,
      languageCode: lang,
      theme,
      google_target_id: shouldLoadOverseaAnalytics ? GOOGLE_TARGET_ID : '',
      apiConfig: {
        middlewareConfig: {
          errorMiddleware: createErrorMiddleware,
          logging: true,
          commonParamsHeadersMiddleware: createCommonParamsMiddleware(() => {
            const context = buildTikTokContext();
            return {
              tt_ttclid: context.ttclid,
              tt_ttp: context.ttp,
              first_url: context.first_url,
              page_url: context.page_url,
              referrer: context.referrer,
              pixel_ready: context.pixel_ready ? 1 : 0,
              extra: { big_data_patch: { position_type: window.location.pathname } },
            };
          }),
        },
        canvasMiddlewareConfig: {
          errorMiddleware: createCanvasErrorMiddleware,
          logging: true,
        },
      },
      sentryConfig: {
        SDK_VERSION: Sentry.SDK_VERSION,
        startInactiveSpan: Sentry.startInactiveSpan,
        captureMessage: Sentry.captureMessage,
      },
      userInfo: {
        uid: (uid && token) ? uid : '',
        token: (uid && token) ? token : '',
        avatar: '',
        screenName: '',
      },
    });
    await initMTLogin({ isOversea: shouldLoadOverseaAnalytics });

    InitNavigateMap({
      historyStudio: (query) => {
        navigate({
          mode: 'url',
          url: getEnv().ENV === 'development' ? '/--roboneo/studio' : '/studio',
          query,
        });
      },
      agentCreateStudio: (query) => {
        navigate({
          mode: 'url',
          url: getEnv().ENV === 'development' ? '/--roboneo/studio' : '/studio',
          query,
        });
      },
      historyFlow: (query) => {
        navigate({
          mode: 'url',
          url: getEnv().ENV === 'development' ? '/--roboneo/ai_flow' : '/ai_flow',
          query,
        });
      },
      historyCommunity: (query) => {
        navigate({
          mode: 'url',
          url: getEnv().ENV === 'development' ? '/--roboneo/community' : '/community',
          query,
        });
      },
      historyPPT: (query) => {
        navigate({
          mode: 'url',
          openNewWindow: pageAgent.getCurrentPage()?.locationName === 'home',
          url: getEnv().ENV === 'development' ? '/--roboneo/ppt' : '/ppt',
          query,
        });
      },
      inputChatNewStudio: (query) => {
        navigate({
          mode: 'url',
          url: getEnv().ENV === 'development' ? '/--roboneo/studio' : '/studio',
          query,
        });
      },
      closePPTBackStudio: (query) => {
        navigate({
          mode: 'url',
          url: getEnv().ENV === 'development' ? '/--roboneo/studio' : '/studio',
          query,
          replace: true,
        });
      },
      agentLogoHome: (query) => {
        navigate({
          mode: 'url',
          url: getEnv().ENV === 'development' ? '/--roboneo/home' : '/home',
        });
      },
      onCheckRoomError: () => {
        // room_id 检测无效回调
      },
      historyCurrentRoomDelete: () => {
        navigate({
          mode: 'url',
          url: getEnv().ENV === 'development' ? '/--roboneo/home' : '/home',
          replace: true,
        });
      },
      history404: () => {
        navigate({
          mode: 'url',
          url: getEnv().ENV === 'development' ? '/--roboneo/404' : '/404',
        });
      },
      historyTeamFlow: (query) => {
        navigateWithTeamStudioCover({
          mode: 'url',
          url: getEnv().ENV === 'development' ? '/--roboneo/team_studio' : '/team_studio',
          query,
        });
      },
      caseDetail: () => {},
    });
    return {};
  },
});
